/* eslint-disable camelcase */
/* eslint-disable react/display-name */
/**
 * @file PatientsManagementView.jsx
 * @author Fernando Ruano
 * @description Patients Management View Page
 */

// Dependencies
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Menu,
  Dropdown,
  Tooltip,
  Popconfirm,
  notification,
  Input,
  Spin
} from 'antd';
import {
  MailOutlined,
  KeyOutlined,
  MoreOutlined,
  EditOutlined,
  QuestionCircleOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { useHistory, useLocation } from 'react-router-dom';
import ButtonFilled from '@nephro/components/ButtonFilled';

// Helpers
import { sortString } from '@nephro/commons/helpers/utils';

// Components
import ManagementCrud from '@nephro/components/ManagementCrud';

// Styles
import { setTableConfig } from 'redux/stores/app/actions';
import { useDispatch } from 'react-redux';
import styles from './styles.module.css';
import {
  patientsManagementTableSearchableFields,
  patientstatusFilters
} from './enums';

/**
 * Patients Management View
 * @param { Object } patientActions - Patient Actions.
 * @return { Node } React node.
 */
function PatientsManagementView({
  userActions,
  patientActions,
  authActions,
  dashboardStaffActions,
  actions,
  loading,
  user
}) {
  const dispatch = useDispatch();
  const DEFAULT_FILTER_VALUE = ' ';
  const [rowData, setRowData] = React.useState([]);
  const [currentPage] = React.useState(1);
  const [filter] = React.useState(DEFAULT_FILTER_VALUE);
  const [selectedRows, setSelectedRows] = React.useState([]);
  const [isAllowedToShow, setIsAllowedToShow] = React.useState(false);
  const [totalItems, setTotalItems] = React.useState(0);
  const [patientStatusList, setPatientStatusList] = React.useState(null);
  const { t: translate } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const [filterData, setFilterData] = React.useState(null);
  const { getAdmonCodesByEntity } = dashboardStaffActions;
  const [searchTerm, setSearchTerm] = React.useState('');
  const [loadingFirstData, setLoadingFirstData] = React.useState(false);

  const {
    deletePatientById,
    createPatientOkta,
    updatePatient,
    getPatientStatusArray,
    getPatientsArray,
    sendMassInvitation
  } = patientActions;

  const { recoverPassword } = authActions;
  const { getValidatePermission } = userActions;
  const { setLoading } = actions;

  const { clinicsFilter, insurersFilter, activeFilters } = useMemo(() => {
    const uniqueClinics = new Set();
    const uniqueInsurers = new Set();
    const validPatientStatuses = new Set();

    if (filterData?.length) {
      filterData.forEach((row) => {
        if (row.aseguradora) uniqueClinics.add(row.aseguradora);
        if (row.centro) uniqueInsurers.add(row.centro);

        if (row.patient_status) {
          const isValidStatus = patientstatusFilters.some(
            (filterPatient) => filterPatient.value === row.patient_status
          );
          if (isValidStatus) validPatientStatuses.add(row.patient_status);
        }
      });
    }

    return {
      clinicsFilter: Array.from(uniqueClinics).map((clinic) => ({
        text: clinic,
        value: clinic
      })),
      insurersFilter: Array.from(uniqueInsurers).map((insurer) => ({
        text: insurer,
        value: insurer
      })),
      activeFilters: patientstatusFilters.filter((filterPatient) =>
        validPatientStatuses.has(filterPatient.value)
      )
    };
  }, [filterData]);

  // Function to get and set patient status list
  const onGetPatientStatusList = (_, response) => {
    if (response?.data) {
      setPatientStatusList(response.data);
    }
  };

  // Set data response
  const onGetRowData = (_, response) => {
    const content = response?.data;
    if (content) {
      if (content?.length !== totalItems) {
        setTotalItems(content?.length);
      }

      const data = content.map((itm, idx) => {
        return {
          ...itm,
          patient_status: itm?.patient_status
            ? translate(`patientMg.${itm?.patient_status}`)
            : translate(`patientMg.inactivo`),
          tipoDocumento: itm?.identification_type?.name,
          documento: itm?.id_document + idx
        };
      });
      setRowData(data);
      setLoadingFirstData(false);
    }
  };

  // Get patients list
  const onGetPatients = async () => {
    setLoading(true);
    setLoadingFirstData(true);
    await getPatientsArray({}, onGetRowData);
  };

  const onUpdatePatient = () => {
    const page = filter === ' ' || !filter ? currentPage - 1 : 0;
    onGetPatients(page, filter);
  };

  useEffect(() => {
    setLoadingFirstData(true);
    const entityId = 3;
    getPatientStatusArray(onGetPatientStatusList);
    getAdmonCodesByEntity({ entityId });

    const getData = () => {
      return {
        params: {
          moduleName: 'Pacientes',
          userName: user.username
        }
      };
    };
    getValidatePermission(getData(), (status, res) => {
      if (status === 'success') {
        setIsAllowedToShow(res.massive);
      }
    });
  }, []);

  useEffect(() => {
    if (user.username) {
      const getData = () => {
        return {
          params: {
            moduleName: 'Pacientes',
            userName: user.username
          }
        };
      };
      getValidatePermission(getData(), (status, res) => {
        if (status === 'success') {
          setIsAllowedToShow(res.massive);
        }
      });
    }
  }, [user]);

  const afterInvite = (type, _, item) => {
    if (type === 'success') {
      notification.success({
        message: translate('registration.notification'),
        description: translate('patientMg.invitation')
      });
      const guestStatus = patientStatusList.filter(
        (itm) => itm?.description === 'guest'
      );
      const data = {
        ...item,
        id: item.id_patient,
        patient_status: guestStatus[0]
      };
      updatePatient(data, onUpdatePatient);
    }
  };

  // Invite selected patient by id
  const onInvite = (item) => {
    const { first_name, last_name, email } = item;
    const data = {
      first_name,
      last_name,
      email
    };
    if (item.patient_status !== translate('patientMg.inactivo')) {
      notification.warning({
        message: translate('registration.notification'),
        description: translate('patientMg.alreadyInvited'),
        duration: 5
      });
    } else {
      createPatientOkta(data, (type, response) =>
        afterInvite(type, response, item)
      );
    }
  };

  const onValidate = (type, response) => {
    if (type === 'success') {
      notification.success({
        message: translate('registration.notification'),
        description: translate('passwordRecovery.recoveryPatient'),
        duration: 10
      });
    } else {
      const { code } = response;
      notification.error({
        message: translate('registration.notification'),
        description: code !== undefined ? `errors.${code}` : 'commons.problem',
        duration: 10
      });
    }
  };

  const onResetPassword = (item) => {
    const { email } = item;
    const data = {
      email
    };
    recoverPassword(data, onValidate);
  };

  // Navigate to route after click on action
  const onNavigateTo = (url) => {
    const baseUrl = location.pathname;
    const lastCharURL = baseUrl[baseUrl.length - 1];
    const goTo = lastCharURL === '/' ? `${url}` : `/${url}`;
    history.push(`${location.pathname}${goTo}`);
  };

  // Navigate to route on Mobile View
  const onNavigateToMobile = (url, id, item) => {
    if (url === 'invite') {
      return onInvite(item);
    }
    return onNavigateTo(`${url}/${id}`);
  };

  const menu = (id, item) => (
    <Menu onClick={(e) => onNavigateToMobile(e.key, id, item)}>
      {item.email && (
        <Menu.Item key="invite">{translate('patientMg.invite')}</Menu.Item>
      )}
      {item.patient_status === translate('patientMg.activo') && (
        <Menu.Item key="reset_pass">{translate('commons.resetPass')}</Menu.Item>
      )}
      <Menu.Item key="edit">{translate('commons.edit')}</Menu.Item>
    </Menu>
  );

  // Table columns
  const columns = [
    {
      title: translate('patientMg.name'),
      dataIndex: 'first_name',
      sorter: sortString('first_name')
    },
    {
      title: translate('patientMg.lastName'),
      dataIndex: 'last_name',
      sorter: sortString('last_name')
    },
    {
      title: translate('patientMg.documentType'),
      dataIndex: 'tipoDocumento',
      sorter: sortString('tipoDocumento'),
      ellipsis: true
    },
    {
      title: 'ID',
      dataIndex: 'id_document',
      sorter: sortString('id_document')
    },
    {
      title: translate('patientMg.email'),
      dataIndex: 'email',
      sorter: sortString('email')
    },
    {
      title: translate('patientMg.phoneNumber'),
      dataIndex: 'contact_phone_number',
      sorter: sortString('contact_phone_number')
    },
    {
      title: translate('patientMg.status'),
      dataIndex: 'patient_status',
      sorter: sortString('patient_status'),
      filters: activeFilters?.length > 0 ? activeFilters : null,
      filterSearch: true
    },
    {
      title: translate('patientMg.clinic'),
      dataIndex: 'aseguradora',
      sorter: sortString('aseguradora'),
      filters: clinicsFilter?.length > 0 ? clinicsFilter : null,
      filterSearch: true,
      filterMode: 'tree'
    },
    {
      title: translate('patientMg.insurance'),
      dataIndex: 'centro',
      sorter: sortString('centro'),
      filters: insurersFilter?.length > 0 ? insurersFilter : null,
      filterSearch: true,
      filterMode: 'tree'
    },
    {
      title: translate('patientMg.modality'),
      dataIndex: 'modalidad',
      sorter: sortString('modalidad')
    },
    {
      title: translate('commons.actions'),
      width: 150,
      dataIndex: 'actions',
      fixed: 'right',
      render: (_, item) => {
        const id = item.id_patient;
        return (
          <div>
            <div className="w-full min-w-min max-w-2 space-x-5 items-start hidden md:flex">
              {item.email &&
                item.contact_phone_number &&
                item.patient_status !== translate('patientMg.activo') && (
                  <Popconfirm
                    title={translate('patientMg.invitePatient')}
                    cancelText={translate('commons.cancel')}
                    okText={translate('commons.confirm')}
                    onConfirm={() => onInvite(item)}
                    icon={
                      <QuestionCircleOutlined style={{ color: '#FA7268' }} />
                    }>
                    <Tooltip title={translate('patientMg.invite')}>
                      <MailOutlined className={styles.buttonIcon} />
                    </Tooltip>
                  </Popconfirm>
                )}
              {item.patient_status === translate('patientMg.activo') && (
                <Popconfirm
                  title={translate('patientMg.resetPasswordPatient')}
                  cancelText={translate('commons.cancel')}
                  okText={translate('commons.confirm')}
                  onConfirm={() => onResetPassword(item)}
                  icon={
                    <QuestionCircleOutlined style={{ color: '#FA7268' }} />
                  }>
                  <Tooltip title={translate('commons.resetPass')}>
                    <KeyOutlined className={styles.buttonIcon} />
                  </Tooltip>
                </Popconfirm>
              )}
              <Tooltip title={translate('commons.edit')}>
                <EditOutlined
                  onClick={() => onNavigateTo(`edit/${id}`)}
                  className={styles.buttonIcon}
                />
              </Tooltip>
            </div>
            <div className="md:hidden flex justify-center items-center">
              <Dropdown overlay={() => menu(id, item)} trigger={['click']}>
                <Button
                  key="3"
                  className={styles.moreButton}
                  icon={<MoreOutlined className={styles.buttonIcon} />}
                />
              </Dropdown>
            </div>
          </div>
        );
      }
    }
  ];

  const handleMassiveInvite = async () => {
    const cleanRows = selectedRows.map((row) => {
      return row.split('-')[0];
    });

    const targetRows = rowData.filter((row) => {
      return cleanRows.some((cleanRow) => {
        return cleanRow.includes(row.id_document);
      });
    });

    const massiveInvitePayload = {
      current_user: `${user.first_name} ${user.last_name}`,
      email_user: user.email,
      selected_patients: targetRows
    };
    setSelectedRows([]);
    dispatch(setTableConfig({ resetSelectedRows: true }));
    await sendMassInvitation(massiveInvitePayload, () => {});
  };

  return (
    <div className="h-full">
      {rowData && (
        <>
          <div className="flex justify-between items-center mt-4 mx-3">
            <div>
              {
                <Input
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                  }}
                  prefix={
                    <SearchOutlined
                      style={{
                        fontSize: 16,
                        color: 'var(--color-grayFontInput)'
                      }}
                    />
                  }
                  placeholder={translate('commons.search')}
                  allowClear
                  className={styles.inputSearch}
                />
              }
            </div>
            {isAllowedToShow ? (
              <div className="flex space-x-4">
                <ButtonFilled
                  textButton="Invitación masiva"
                  disabled={selectedRows.length <= 0}
                  onActionClick={() => handleMassiveInvite()}
                />
                <ButtonFilled
                  textButton="Procesos"
                  onActionClick={() => {
                    onNavigateTo('massive-processing');
                  }}
                />
              </div>
            ) : null}
          </div>
          <Spin spinning={loadingFirstData && !loading}>
            <div className="flex flex-wrap">
              <div className="w-full overflow-x-scroll">
                <ManagementCrud
                  getItemsArray={() =>
                    setTimeout(() => {
                      onGetPatients();
                    }, 1500)
                  }
                  onRowSelection={(rows) => setSelectedRows(rows)}
                  deleteItemById={deletePatientById}
                  columns={columns}
                  customMapDataAfterDelete={onGetRowData}
                  deleteConfirmText={'patientMg.deletePatient'}
                  itemIdKey={'id_patient'}
                  rowData={rowData}
                  withDefaultActions={false}
                  search={false}
                  setFilterData={setFilterData}
                  searchableKeys={patientsManagementTableSearchableFields}
                  searchTerm={searchTerm}
                  hasRowSelection
                  isVirtualPagination
                  withPageSize
                />
              </div>
            </div>
          </Spin>
        </>
      )}
    </div>
  );
}

/**
 * Component definition of proptypes.
 */
PatientsManagementView.propTypes = {
  userActions: PropTypes.shape({
    getValidatePermission: PropTypes.func.isRequired
  }).isRequired,
  actions: PropTypes.shape({
    setLoading: PropTypes.func.isRequired
  }),
  authActions: PropTypes.shape({
    recoverPassword: PropTypes.func.isRequired
  }).isRequired,
  patientActions: PropTypes.shape({
    getFilterPatients: PropTypes.func.isRequired,
    deletePatientById: PropTypes.func.isRequired,
    createPatientOkta: PropTypes.func.isRequired,
    updatePatient: PropTypes.func.isRequired,
    getPatientStatusArray: PropTypes.func.isRequired,
    getPatientsArray: PropTypes.func.isRequired,
    sendMassInvitation: PropTypes.func.isRequired
  }).isRequired,
  dashboardStaffActions: PropTypes.shape({
    getAdmonCodesByEntity: PropTypes.func.isRequired
  }).isRequired,
  clinicId: PropTypes.number,
  lastVersiaUpdate: PropTypes.string,
  user: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]).isRequired,
  loading: PropTypes.bool
};

export default PatientsManagementView;
